import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCommentsGateway } from './CommentsGatewayProvider';
import { useOptimisticComment } from './useOptimisticComment';
import { insertOptimisticComment } from '../api/commentCache';
import { getCommentsV2QueryOptions } from '../api/queryOptions';
import { anonymousAliasFor } from '../model/anonymousAlias';
import type { Comment } from '../model/types';

import { useUserScopedKey } from '@/hooks/useUserScopedQuery';
import { trackCommentCreated, trackCommentCreateFailed } from '@/util/analyticsEvents';

export type CreateCommentVariables = {
  postId: number;
  body: string;
  /** The comment a reply answers. */
  replyToId?: number;
  /** The top-level comment a reply sits under, which can differ from the one it answers. */
  parentId?: number | null;
  isAnonymous: boolean;
};

/**
 * Creates a comment or reply: shows it at once, rolls it back if the request
 * fails, and refreshes the thread once it is saved. The one path every
 * composer goes through.
 *
 * `onPosted` and `onFailed` are where a caller adds its own steps, such as
 * refreshing the post or scrolling to the new comment. They are hook-level,
 * so they run for every submit, a quick second one included.
 */
export function useCreateComment({
  onPosted,
  onFailed,
}: {
  onPosted?: (variables: CreateCommentVariables) => void;
  onFailed?: (error: Error) => void;
} = {}) {
  const queryClient = useQueryClient();

  const commentsKey = useUserScopedKey(getCommentsV2QueryOptions);

  const gateway = useCommentsGateway();

  const buildOptimisticComment = useOptimisticComment();

  return useMutation<
    void,
    Error,
    CreateCommentVariables,
    { previousComments: Comment[]; postId: number }
  >({
    mutationFn: ({ postId, body, replyToId, isAnonymous }) =>
      gateway.createComment({ postId, body, replyToId: replyToId ?? null, isAnonymous }),
    onMutate: async ({ postId, body, replyToId, parentId, isAnonymous }) => {
      const optimistic = buildOptimisticComment({
        body,
        parentId: replyToId ? (parentId ?? null) : null,
        replyToId: replyToId ?? null,
        isAnonymous,
      });

      const queryKey = commentsKey(postId);

      await queryClient.cancelQueries({ queryKey });

      const previousComments = queryClient.getQueryData(queryKey) ?? [];

      const shown = isAnonymous
        ? { ...optimistic, anonymousAlias: anonymousAliasFor(previousComments, optimistic.userId) }
        : optimistic;

      queryClient.setQueryData(
        queryKey,
        insertOptimisticComment(previousComments, shown, { replyToId, parentId })
      );

      return { previousComments, postId };
    },
    onError: (error, variables, context) => {
      trackCommentCreateFailed({
        postId: variables.postId,
        replyToId: variables.replyToId ?? null,
        error: error instanceof Error ? error.message : JSON.stringify(error),
        isAnonymous: variables.isAnonymous,
      });

      if (context) {
        queryClient.setQueryData(commentsKey(context.postId), context.previousComments);
      }

      onFailed?.(error);
    },
    onSuccess: (_data, variables) => {
      trackCommentCreated({
        postId: variables.postId,
        replyToId: variables.replyToId ?? null,
        bodyLength: variables.body.length,
        isAnonymous: variables.isAnonymous,
      });

      void queryClient.invalidateQueries({
        queryKey: commentsKey(variables.postId),
      });

      onPosted?.(variables);
    },
  });
}
